import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { ReactComponent as BigPinkStar } from './svgs/big-pink-star.svg';
import { ReactComponent as BlueStar } from './svgs/blue-star.svg';
import { ReactComponent as LittlePinkStar } from './svgs/little-pink-star.svg';
import { ReactComponent as Telly } from './svgs/telly.svg';
import { ReactComponent as TellyRunning } from './svgs/telly-running.svg';
import { ReactComponent as GreenStar } from './svgs/green-star.svg';
import { ReactComponent as YellowStarSpikey } from './svgs/yellow-star-spikey.svg';
import { ReactComponent as YellowStar1 } from './svgs/yellow-star-1.svg';
import { ReactComponent as YellowStar2 } from './svgs/yellow-star-2.svg';
import { ReactComponent as YellowStar3 } from './svgs/yellow-star-3.svg';
import { GreenroomState } from '../../stores/types/types';
import { handleClickJacking, logOut } from '../../utils/utils';
import { LANDING_PAGE_TYPES } from '../../types/constants';

interface Props {
	type: string;
}

interface LandingPageType {
	title: string;
	subTitle: string | JSX.Element;
	buttonText: string;
	className: string;
	tellySvg: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
}

interface LandingPageTypes {
	[key: string]: LandingPageType;
}

const landingPageTypes: LandingPageTypes = {
	accountDeleted: {
		title: 'You’re welcome any time!',
		subTitle: 'Account was successfully deleted',
		buttonText: 'Back to Main Page',
		className: 'account-deleted',
		tellySvg: Telly,
	},
	unhandledThrownError: {
		title: 'An error has occurred',
		subTitle: (
			<>
				<span>Please refresh your browser</span>
				<span>If the problem persists, call Brandlive support at: <b>1 (866) 532-2154</b></span>
				<span>or email us at <a href="mailto:support@brandlive.com">support@brandlive.com</a></span>
			</>
		),
		buttonText: '',
		className: 'unhandled-thrown-error',
		tellySvg: TellyRunning,
	},
};

export default function LandingPage(props: Props) {
	const { type } = props;
	const host = useSelector((state: GreenroomState) => state.SessionReducer.host);

	useEffect(() => {
		handleClickJacking();
	}, [])

	const handleClick = async () => {
		if (type === LANDING_PAGE_TYPES.ACCOUNT_DELETED) {
			logOut(host, false);
		}
	};

	const TellySvg = useMemo(() => landingPageTypes[type].tellySvg, [type]);

	// NOTE: Inside all of these svgs is a <pattern> with an id attribute.
	// When I copied them from Figma they were all id="pattern0" and they would overwrite eachother,
	// so I renamed the ids to the name of the file.
	// The id is also used in the <rect> fill attribute in these svgs and those need to be the same.
	return (
		<div className={classNames('landing-page', landingPageTypes[type].className)}>
			<div className="background">

				<div className="text-container">
					<span className="title">{landingPageTypes[type].title}</span>
					<span className="sub-title">{landingPageTypes[type].subTitle}</span>
				</div>

				<div className="telly-glow" />

				<TellySvg className='telly' />
				<BigPinkStar className='big-pink-star' />
				<LittlePinkStar className='little-pink-star' />
				<BlueStar className='blue-star' />
				<GreenStar className='green-star' />
				<YellowStarSpikey className='yellow-star-spikey' />
				<YellowStar1 className='yellow-star-1-1' />
				<YellowStar1 className='yellow-star-1-2' />
				<YellowStar2 className='yellow-star-2-1' />
				<YellowStar2 className='yellow-star-2-2' />
				<YellowStar2 className='yellow-star-2-3' />
				<YellowStar3 className='yellow-star-3' />

				{landingPageTypes[type].buttonText && (
					<button className='back-button' onClick={handleClick}>
						<span>{landingPageTypes[type].buttonText}</span>
					</button>
				)}

			</div>
		</div>
	);
}
